<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';
import { stepColor } from 'kanban/constants/reports';
import {
  formatNumber,
  formatPercent,
  formatCurrencyValue,
  formatDuration,
} from 'kanban/helper/reportFormatters';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  locale: {
    type: String,
    default: 'en',
  },
});

const emit = defineEmits(['retry']);

const { t } = useI18n();

const steps = computed(() => props.data?.steps || []);
const currency = computed(() => props.data?.currency);

const maxEntered = computed(() =>
  Math.max(1, ...steps.value.map(step => step.entered_count || 0))
);

const barWidth = step =>
  `${Math.max(3, ((step.entered_count || 0) / maxEntered.value) * 100)}%`;

const statusLabel = status => {
  if (status === 'completed') return t('KANBAN.STATUS.COMPLETED');
  if (status === 'cancelled') return t('KANBAN.STATUS.CANCELLED');
  return null;
};

const conversionLabel = step =>
  step.conversion_to_next === null || step.conversion_to_next === undefined
    ? null
    : formatPercent(step.conversion_to_next, props.locale);
</script>

<template>
  <KanbanReportWidget
    :title="t('KANBAN.REPORTS.FUNNEL.TITLE')"
    :info-text="t('KANBAN.REPORTS.FUNNEL.INFO')"
    :is-loading="isLoading"
    :error="error"
    :is-empty="!steps.length"
    :empty-text="t('KANBAN.REPORTS.FUNNEL.EMPTY')"
    @retry="emit('retry')"
  >
    <div class="flex flex-col gap-2">
      <div
        v-for="(step, index) in steps"
        :key="step.step_id"
        class="flex flex-col gap-2"
      >
        <div class="flex flex-col gap-1.5">
          <div class="flex items-center justify-between gap-3 text-sm">
            <div class="flex items-center gap-2 min-w-0">
              <span
                class="w-2.5 h-2.5 rounded-full flex-shrink-0"
                :style="{ backgroundColor: stepColor(step.color, index) }"
              />
              <span class="font-medium text-n-slate-12 truncate">
                {{ step.name }}
              </span>
              <span
                v-if="statusLabel(step.status)"
                class="px-1.5 py-0.5 rounded text-xs text-n-slate-11 bg-n-slate-3 flex-shrink-0"
              >
                {{ statusLabel(step.status) }}
              </span>
            </div>
            <div
              class="flex items-center gap-4 flex-shrink-0 text-xs text-n-slate-11"
            >
              <span v-tooltip="t('KANBAN.REPORTS.FUNNEL.AVG_TIME')">
                <i class="i-lucide-clock w-3 h-3 inline-block align-middle" />
                {{ formatDuration(step.avg_time_in_step_seconds, t) }}
              </span>
              <span
                v-if="step.pipeline_value != null"
                v-tooltip="t('KANBAN.REPORTS.FUNNEL.PIPELINE')"
              >
                {{
                  formatCurrencyValue(step.pipeline_value, currency, locale, {
                    compact: true,
                  })
                }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <div class="flex-1 h-7 rounded-md bg-n-slate-2 overflow-hidden">
              <div
                class="h-full rounded-md flex items-center px-2 transition-all"
                :style="{
                  width: barWidth(step),
                  backgroundColor: stepColor(step.color, index),
                }"
              >
                <span class="text-xs font-semibold text-white/90 tabular-nums">
                  {{ formatNumber(step.entered_count, locale) }}
                </span>
              </div>
            </div>
            <span class="text-xs text-n-slate-10 tabular-nums w-28 text-right">
              {{
                t('KANBAN.REPORTS.FUNNEL.CURRENT', {
                  count: formatNumber(step.current_count, locale),
                })
              }}
            </span>
          </div>
        </div>
        <div
          v-if="index < steps.length - 1 && conversionLabel(step)"
          class="flex items-center gap-1 pl-1 text-xs text-n-slate-10"
        >
          <i class="i-lucide-corner-down-right w-3.5 h-3.5" />
          <span>
            {{
              t('KANBAN.REPORTS.FUNNEL.CONVERSION', {
                rate: conversionLabel(step),
              })
            }}
          </span>
        </div>
      </div>
    </div>
  </KanbanReportWidget>
</template>
